import { $generateHtmlFromNodes } from '@lexical/html'
import { LinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { INSERT_ORDERED_LIST_COMMAND, INSERT_UNORDERED_LIST_COMMAND, ListItemNode, ListNode } from '@lexical/list'
import { LexicalComposer } from '@lexical/react/LexicalComposer'
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import { ContentEditable } from '@lexical/react/LexicalContentEditable'
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary'
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin'
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin'
import { ListPlugin } from '@lexical/react/LexicalListPlugin'
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin'
import { ListBullets, ListNumbers, LinkSimple, TextB, TextItalic, TextUnderline } from '@phosphor-icons/react'
import { $createParagraphNode, $getRoot, CLEAR_HISTORY_COMMAND, FORMAT_TEXT_COMMAND, type LexicalEditor } from 'lexical'
import { useEffect, useState, type CSSProperties } from 'react'
import { Button } from 'app/components/ui/button'
import { Input } from 'app/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from 'app/components/ui/popover'
import { logError } from 'lib/logging'

const ALLOWED_URL = /^(https?:\/\/|mailto:)\S+$/i

const EDITOR_CONFIG = {
  namespace: `EmailBodyEditor`,
  nodes: [ListNode, ListItemNode, LinkNode],
  theme: {
    text: { bold: `font-semibold`, italic: `italic`, underline: `underline` },
    list: { ul: `list-disc pl-5`, ol: `list-decimal pl-5`, listitem: ``, nested: { listitem: `list-none` } },
    link: `text-primary-600 underline`,
  },
  onError: (error: Error) => logError(`EmailBodyEditor error`, error),
}

function isAllowedUrl(url: string) {
  return ALLOWED_URL.test(url)
}

function LinkButton({ editor, disabled }: { editor: LexicalEditor; disabled: boolean }) {
  const [open, setOpen] = useState(false)
  const [url, setUrl] = useState(`https://`)

  const trimmed = url.trim()
  const valid = isAllowedUrl(trimmed)

  const apply = () => {
    if (!valid) return
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, trimmed)
    setOpen(false)
    setUrl(`https://`)
    editor.focus()
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="subtle"
          size="icon-sm"
          title="Link"
          aria-label="Link"
          disabled={disabled}
          onMouseDown={(evt) => evt.preventDefault()}
        >
          <LinkSimple className="h-4 w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        onCloseAutoFocus={(evt) => {
          evt.preventDefault()
          editor.focus()
        }}
      >
        <Input
          aria-label="Link address"
          value={url}
          onChange={(evt) => setUrl(evt.target.value)}
          onKeyDown={(evt) => {
            if (evt.key !== `Enter`) return
            evt.preventDefault()
            apply()
          }}
        />
        <Button type="button" size="sm" disabled={!valid} onClick={apply}>
          Add link
        </Button>
      </PopoverContent>
    </Popover>
  )
}

function Toolbar({ editable }: { editable: boolean }) {
  const [editor] = useLexicalComposerContext()
  const tools = [
    {
      label: `Bold`,
      icon: <TextB className="h-4 w-4" weight="bold" />,
      run: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, `bold`),
    },
    {
      label: `Italic`,
      icon: <TextItalic className="h-4 w-4" />,
      run: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, `italic`),
    },
    {
      label: `Underline`,
      icon: <TextUnderline className="h-4 w-4" />,
      run: () => editor.dispatchCommand(FORMAT_TEXT_COMMAND, `underline`),
    },
  ]
  const lists = [
    {
      label: `Bulleted list`,
      icon: <ListBullets className="h-4 w-4" />,
      run: () => editor.dispatchCommand(INSERT_UNORDERED_LIST_COMMAND, undefined),
    },
    {
      label: `Numbered list`,
      icon: <ListNumbers className="h-4 w-4" />,
      run: () => editor.dispatchCommand(INSERT_ORDERED_LIST_COMMAND, undefined),
    },
  ]

  return (
    <div className="flex items-center gap-0.5 border-b border-default px-2 py-1">
      {tools.map((tool) => (
        <ToolbarButton key={tool.label} disabled={!editable} {...tool} />
      ))}
      <LinkButton editor={editor} disabled={!editable} />
      {lists.map((tool) => (
        <ToolbarButton key={tool.label} disabled={!editable} {...tool} />
      ))}
    </div>
  )
}

function ToolbarButton({
  label,
  icon,
  run,
  disabled,
}: {
  label: string
  icon: React.ReactNode
  run: () => void
  disabled: boolean
}) {
  return (
    <Button
      type="button"
      variant="subtle"
      size="icon-sm"
      title={label}
      aria-label={label}
      disabled={disabled}
      onMouseDown={(evt) => evt.preventDefault()}
      onClick={run}
    >
      {icon}
    </Button>
  )
}

function EmitPlugin({
  editable,
  onChange,
  onReady,
}: {
  editable: boolean
  onChange: (html: string, isEmpty: boolean) => void
  onReady?: (clear: () => void) => void
}) {
  const [editor] = useLexicalComposerContext()

  useEffect(() => {
    editor.setEditable(editable)
  }, [editor, editable])

  useEffect(
    () =>
      editor.registerUpdateListener(({ editorState, dirtyElements, dirtyLeaves }) => {
        if (dirtyElements.size === 0 && dirtyLeaves.size === 0) return
        editorState.read(() => {
          const isEmpty = $getRoot().getTextContent().trim() === ``
          onChange(isEmpty ? `` : $generateHtmlFromNodes(editor), isEmpty)
        })
      }),
    [editor, onChange],
  )

  useEffect(() => {
    onReady?.(() =>
      editor.update(() => {
        const root = $getRoot()
        root.clear()
        root.append($createParagraphNode())
        editor.dispatchCommand(CLEAR_HISTORY_COMMAND, undefined)
      }),
    )
  }, [editor, onReady])

  return null
}

export function EmailBodyEditor({
  editable,
  onChange,
  onReady,
}: {
  editable: boolean
  onChange: (html: string, isEmpty: boolean) => void
  onReady?: (clear: () => void) => void
}) {
  return (
    <LexicalComposer initialConfig={EDITOR_CONFIG}>
      <Toolbar editable={editable} />
      <div className="relative">
        <RichTextPlugin
          contentEditable={
            <ContentEditable
              aria-label="Email message"
              style={{ '--email-body-max': `40vh` } as CSSProperties}
              className="min-h-24 max-h-(--email-body-max) overflow-y-auto px-3 py-2 text-body-md text-strong outline-none"
            />
          }
          placeholder={
            <div className="pointer-events-none absolute left-3 top-2 select-none text-body-md text-minimal">
              Write your email
            </div>
          }
          ErrorBoundary={LexicalErrorBoundary}
        />
        <HistoryPlugin />
        <ListPlugin />
        <LinkPlugin validateUrl={isAllowedUrl} />
        <EmitPlugin editable={editable} onChange={onChange} onReady={onReady} />
      </div>
    </LexicalComposer>
  )
}
